import * as signalR from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { toStateArray } from '../model/state.model';
import { isPlayerStateNullMessage, isPlayerStateNullState } from './player-state-error';
import * as i0 from "@angular/core";
var SignalRService = /** @class */ (function () {
    function SignalRService() {
        var _this = this;
        this.statesToProcess = new BehaviorSubject(null);
        this.connectionStartPromise = null;
        this.reconnectingForUser = null;
        this.startConnection = function (username) {
            if (_this.hubConnection && _this.hubConnection.state === HubConnectionState.Connected) {
                return Promise.resolve();
            }
            if (_this.connectionStartPromise !== null && _this.reconnectingForUser === username) {
                return _this.connectionStartPromise;
            }
            _this.hubConnection = new signalR.HubConnectionBuilder()
                .withUrl(ApiService.baseUrl + "/AppHub?username=" + username, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets
            })
                .build();
            _this.addMessageListener();
            _this.reconnectingForUser = username;
            _this.connectionStartPromise = _this.hubConnection.start()
                .finally(function () {
                _this.connectionStartPromise = null;
            });
            return _this.connectionStartPromise;
        };
        this.disconnect = function () {
            if (_this.hubConnection) {
                _this.hubConnection.stop()
                    .then(function () { return console.log('SignalR Connection stopped'); });
            }
            _this.connectionStartPromise = null;
            _this.reconnectingForUser = null;
        };
        this.addMessageListener = function () {
            _this.hubConnection.off('ReceiveMessage');
            _this.hubConnection.on('ReceiveMessage', function (message) {
                try {
                    // console.log('ReceiveMessage', message);
                    if (!message || !message.length) {
                        return;
                    }
                    // Transient "player has no state yet" diagnostic: not actionable for users.
                    if (isPlayerStateNullMessage(message)) {
                        return;
                    }
                    // Some server branches can return plain diagnostic strings; ignore them.
                    if (message.startsWith('MyDjPlayerState_')) {
                        console.warn('ReceiveMessage skipped non-JSON payload', message);
                        return;
                    }
                    // Legacy JSONP wrapper: callbackName(<json>);
                    var firstParenIndex = message.indexOf('(');
                    if (firstParenIndex > 0 && message.endsWith(');')) {
                        message = message.substring(firstParenIndex + 1, message.length - 2);
                    }
                    else if (message[0] === '(' && message.endsWith(');')) {
                        // Legacy variant where payload is wrapped as (<json>);
                        message = message.substring(1, message.length - 2);
                    }
                    // Prefer strict JSON first. Legacy key-normalization fallback is kept for old payloads.
                    var parsed = void 0;
                    try {
                        parsed = JSON.parse(message);
                    }
                    catch (_a) {
                        var correctJson = message.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ');
                        parsed = JSON.parse(correctJson);
                    }
                    // A single state object (`{"id":1,"scheduler":{...}}`) is a valid answer too; consumers
                    // iterate the packet, so normalize it to an array instead of dropping it.
                    var states = toStateArray(parsed);
                    // Ignore transient backend bootstrap status: it is not actionable for users.
                    if (Array.isArray(states) && states.length > 0 && states.every(isPlayerStateNullState)) {
                        return;
                    }
                    // console.log('states', states);
                    _this.statesToProcess.next(states);
                }
                catch (err) {
                    if (isPlayerStateNullMessage(message)) {
                        return;
                    }
                    var scheduler = _this.tryExtractSchedulerFromMalformedPayload(message);
                    if (scheduler) {
                        // Best-effort fallback: keep scheduler page working even when full player state is malformed.
                        _this.statesToProcess.next([{ scheduler: scheduler }]);
                        return;
                    }
                    console.error("ReceiveMessage error", err, message);
                }
            });
        };
        this.sendMessage = function (user, message) {
            // console.log('sendMessage', message);
            if (_this.hubConnection && _this.hubConnection.state === HubConnectionState.Connected) {
                _this.hubConnection.invoke('SendMessage', user, message)
                    .catch(function (err) { return console.error(err); });
            }
            else {
                // Expected during startup or app resume: connection not ready yet; we retry below.
                _this.startConnection(user)
                    .then(function () {
                    return _this.hubConnection.invoke('SendMessage', user, message);
                })
                    .catch(function (err) { return console.error('sendMessage Error establishing SignalR connection: ' + err); });
            }
        };
    }
    SignalRService.prototype.tryExtractSchedulerFromMalformedPayload = function (payload) {
        if (!payload || typeof payload !== 'string') {
            return null;
        }
        var schedulerByKey = this.extractObjectByPropertyName(payload, 'scheduler');
        if (schedulerByKey && schedulerByKey.includes('"weeklySchedule"') && schedulerByKey.includes('"specialDates"')) {
            try {
                return JSON.parse(schedulerByKey);
            }
            catch (_a) {
                var sanitizedByKey = this.sanitizeJsonLikeText(schedulerByKey);
                try {
                    return JSON.parse(sanitizedByKey);
                }
                catch (_b) {
                    // Continue with fallback strategies.
                }
            }
        }
        var schedulerFromSetCommand = this.extractSchedulerFromSetCommandFragment(payload);
        if (schedulerFromSetCommand) {
            try {
                return JSON.parse(schedulerFromSetCommand);
            }
            catch (_c) {
                var sanitizedSetCommand = this.sanitizeJsonLikeText(schedulerFromSetCommand);
                try {
                    return JSON.parse(sanitizedSetCommand);
                }
                catch (_d) {
                    // Continue with fallback strategies.
                }
            }
        }
        var marker = '"version"';
        var versionIdx = payload.indexOf(marker);
        if (versionIdx < 0) {
            return null;
        }
        var schedulerStart = payload.lastIndexOf('{', versionIdx);
        if (schedulerStart < 0) {
            return null;
        }
        var schedulerEnd = this.findMatchingClosingBrace(payload, schedulerStart);
        if (schedulerEnd < 0) {
            return null;
        }
        var candidate = payload.substring(schedulerStart, schedulerEnd + 1);
        if (!candidate.includes('"weeklySchedule"') || !candidate.includes('"specialDates"')) {
            return null;
        }
        try {
            return JSON.parse(candidate);
        }
        catch (_e) {
            var sanitizedCandidate = this.sanitizeJsonLikeText(candidate);
            try {
                return JSON.parse(sanitizedCandidate);
            }
            catch (_f) {
                return null;
            }
        }
    };
    SignalRService.prototype.extractObjectByPropertyName = function (text, propertyName) {
        var marker = "\"" + propertyName + "\"";
        var propIdx = text.indexOf(marker);
        if (propIdx < 0) {
            return null;
        }
        var colonIdx = text.indexOf(':', propIdx + marker.length);
        if (colonIdx < 0) {
            return null;
        }
        var objectStart = text.indexOf('{', colonIdx + 1);
        if (objectStart < 0) {
            return null;
        }
        var objectEnd = this.findMatchingClosingBrace(text, objectStart);
        if (objectEnd < 0) {
            return null;
        }
        return text.substring(objectStart, objectEnd + 1);
    };
    SignalRService.prototype.extractSchedulerFromSetCommandFragment = function (text) {
        var marker = '"id":37,"scheduler"';
        var markerIdx = text.indexOf(marker);
        if (markerIdx < 0) {
            return null;
        }
        var schedulerKeyIdx = text.indexOf('"scheduler"', markerIdx);
        if (schedulerKeyIdx < 0) {
            return null;
        }
        var colonIdx = text.indexOf(':', schedulerKeyIdx + '"scheduler"'.length);
        if (colonIdx < 0) {
            return null;
        }
        var objectStart = text.indexOf('{', colonIdx + 1);
        if (objectStart < 0) {
            return null;
        }
        var objectEnd = this.findMatchingClosingBrace(text, objectStart);
        if (objectEnd < 0) {
            return null;
        }
        var candidate = text.substring(objectStart, objectEnd + 1);
        if (!candidate.includes('"weeklySchedule"') || !candidate.includes('"specialDates"')) {
            return null;
        }
        return candidate;
    };
    SignalRService.prototype.sanitizeJsonLikeText = function (text) {
        if (!text) {
            return text;
        }
        var out = text;
        out = out.replace(/([{,]\s*)([a-zA-Z_]\w*)\s*:/g, '$1"$2":');
        out = out.replace(/:\s*True(?=[,}\]\s])/g, ':true');
        out = out.replace(/:\s*False(?=[,}\]\s])/g, ':false');
        out = out.replace(/:\s*Null(?=[,}\]\s])/g, ':null');
        return out;
    };
    SignalRService.prototype.findMatchingClosingBrace = function (text, openBraceIndex) {
        var depth = 0;
        var inString = false;
        var escaped = false;
        for (var i = openBraceIndex; i < text.length; i++) {
            var ch = text[i];
            if (inString) {
                if (escaped) {
                    escaped = false;
                    continue;
                }
                if (ch === '\\') {
                    escaped = true;
                    continue;
                }
                if (ch === '"') {
                    inString = false;
                }
                continue;
            }
            if (ch === '"') {
                inString = true;
                continue;
            }
            if (ch === '{') {
                depth++;
            }
            else if (ch === '}') {
                depth--;
                if (depth === 0) {
                    return i;
                }
            }
        }
        return -1;
    };
    SignalRService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function SignalRService_Factory() { return new SignalRService(); }, token: SignalRService, providedIn: "root" });
    return SignalRService;
}());
export { SignalRService };
